import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ProductOrPrize, Prize } from '../types/game';
import { useGame } from '../hooks/useGame';
import { FlyingPrize, FlyingDiscount } from '../hooks/usePrizeAnimation';
import { useProductList, Product } from '../hooks/useProducts';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import ProductGrid from '../components/ProductGrid';
import { NUMBER_OF_PRIZES as TOTAL_PRIZES_CONFIG } from '../constants/gameConfig';
import { enablePersistence, isPersistenceEnabled } from '../store/index';
import parentFrameMessaging from '../services/ParentFrameMessaging';
import liljaLogo from '../assets/lilja-logo.png';
import './Game.css';

const FEED_LOADED_STORAGE_KEY = 'playmerce_feed_loaded';
const PRODUCT_ORDER_STORAGE_KEY = 'playmerce_product_order';
const SCROLL_POSITION_STORAGE_KEY = 'playmerce_scroll_position';

interface GameProps {
  feedLoaded: boolean;
  setFeedLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  items: ProductOrPrize[];
  selectedProduct: Product | null;
  handlePrizeClick: (prizeId: string, event: React.MouseEvent<HTMLElement>) => void;
  handleProductClick: (product: Product) => void;
  flyingPrizes: FlyingPrize[] | null;
  flyingDiscounts: FlyingDiscount[] | null;
  walletRef: React.RefObject<HTMLDivElement>;
}

const Game: React.FC<GameProps> = ({
  feedLoaded,
  setFeedLoaded,
  items,
  selectedProduct,
  handlePrizeClick,
  handleProductClick,
  flyingPrizes,
  flyingDiscounts,
  walletRef,
}) => {
  const { settings, loading: settingsLoading, refetch: refetchSettings } = useSettingsRedux(true);
  const navigate = useNavigate();
  const location = useLocation();
  const gameContentRef = useRef<HTMLDivElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const feedProducts = useMemo(() => {
    return items.filter((item) => item.type === 'product').map((item) => item.item as Product);
  }, [items]);

  const {
    loading: gameLoading,
    timeRemaining,
    initializeTimer,
  } = useGame({
    fetchedProducts: feedProducts,
    loadingProducts: false,
    productFetchError: null,
    timeLimitInSeconds: settings?.timeLimitInSeconds,
  });

  useEffect(() => {
    if (isPersistenceEnabled()) {
      localStorage.setItem(FEED_LOADED_STORAGE_KEY, feedLoaded.toString());
    }
  }, [feedLoaded]);

  const handleProductCardClick = (product: Product) => {
    if (isTransitioning) return;

    setIsVisible(false);
    setIsTransitioning(true);
    setTimeout(() => {
      handleProductClick(product);
      setIsTransitioning(false);
    }, 150);
  };

  const handleRulesClick = () => {
    if (isTransitioning) return;

    setIsVisible(false);
    setIsTransitioning(true);
    setTimeout(() => {
      navigate(`${process.env.REACT_APP_ROOT_PATH || ''}/rules`);
      setIsTransitioning(false);
    }, 150);
  };

  const isLoading = gameLoading || settingsLoading || !settings?.timeLimitInSeconds;

  const collectedPrizesCount = items.filter((item) => item.type === 'prize' && item.item.claimed).length;
  const allPrizesCollected = collectedPrizesCount >= TOTAL_PRIZES_CONFIG;

  useEffect(() => {
    enablePersistence();

    if (settings?.timeLimitInSeconds) {
      initializeTimer();
    }

    setIsInitialized(true);
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [initializeTimer, settings?.timeLimitInSeconds]);

  useEffect(() => {
    if (!isInitialized) return;

    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    const isGameRoute = location.pathname === `${rootPath}/` || location.pathname === '/';

    if (isGameRoute) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isInitialized]);

  useEffect(() => {
    if (gameContainerRef.current) {
      gameContainerRef.current.style.opacity = isVisible ? '1' : '0';
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      setIsVisible(false);
    };
  }, []);

  const getGameTitle = () => {
    if (settings?.translations?.feed?.title) {
      return settings.translations.feed.title;
    }
    return 'Collect Lilja Seashells';
  };

  const getGameSubtitle = () => {
    if (settings?.translations?.feed?.objective) {
      return settings.translations.feed.objective;
    }
    return 'Create your dream offer';
  };

  const getRulesButtonText = () => {
    if (settings?.translations?.feed?.rules) {
      return settings.translations.feed.rules;
    }
    return 'Rules';
  };

  useEffect(() => {
    parentFrameMessaging.sendActivateIframe();
  }, []);

  return (
    <div className="game-container" id="game-container" ref={gameContainerRef}>
      <div className="game-content" ref={gameContentRef}>
        <div className="game-header">
          <div className="header-top-row">
            <div className="logo-container">
              <img src={liljaLogo} alt="Lilja Logo" className="game-logo" />
            </div>
            <div className="button-row">
              <button className="rules-button" onClick={handleRulesClick} title={getRulesButtonText()}>
                {getRulesButtonText()}
              </button>
            </div>
          </div>
          <div className="game-title-container">
            <h1 className="game-title">{getGameTitle()}</h1>
            <p className="game-subtitle">{getGameSubtitle()}</p>
          </div>
        </div>
        {isLoading ? (
          <div className="loading-container">Loading all products...</div>
        ) : (
          <ProductGrid
            items={items}
            selectedProduct={selectedProduct}
            handleProductClick={handleProductCardClick}
            handlePrizeClick={handlePrizeClick}
            allPrizesCollected={allPrizesCollected}
          />
        )}
        {flyingPrizes &&
          flyingPrizes.map((prize) => (
            <img
              key={prize.key}
              src={prize.imageUrl}
              alt="Flying Prize"
              className="flying-prize"
              style={
                {
                  top: `${prize.top}px`,
                  left: `${prize.left}px`,
                  '--target-x': `${prize.targetX}px`,
                  '--target-y': `${prize.targetY}px`,
                  animationDelay: prize.animationDelay,
                  animationDuration: prize.animationDuration,
                } as React.CSSProperties
              }
            />
          ))}
        {flyingDiscounts &&
          flyingDiscounts.map((discount) => (
            <img
              key={discount.key}
              src={discount.imageUrl}
              alt="Flying Discount"
              className="flying-discount"
              style={
                {
                  top: `${discount.top}px`,
                  left: `${discount.left}px`,
                  '--target-x': `${discount.targetX}px`,
                  '--target-y': `${discount.targetY}px`,
                  animationDelay: discount.animationDelay,
                  animationDuration: discount.animationDuration,
                } as React.CSSProperties
              }
            />
          ))}
      </div>
    </div>
  );
};

export default Game;
